import { isPlainObject } from 'es-toolkit/predicate';
import { parse as parseUuid } from 'uuid';
import type { JsonValue } from '../../../db/schema';
import { isDate } from '../../utils/date';

const UUID_CANONICAL_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseUuidOrNull(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const trimmed = value.trim();
  if (!UUID_CANONICAL_REGEX.test(trimmed)) {
    return null;
  }

  try {
    parseUuid(trimmed);
    return trimmed.toLowerCase();
  } catch {
    return null;
  }
}

export function serializeToJsonValue(object: unknown): JsonValue {
  if (object === null) {
    return null;
  }
  if (object === undefined) {
    return null;
  }
  if (isDate(object)) {
    return object.toISOString();
  }
  if (typeof object === 'string' || typeof object === 'number' || typeof object === 'boolean') {
    return object;
  }
  if (typeof object === 'bigint') {
    return object.toString();
  }
  if (typeof object === 'symbol') {
    return object.description ?? 'Symbol';
  }
  if (typeof object === 'function') {
    return object.name ? `[Function ${object.name}]` : '[Function]';
  }
  if (Array.isArray(object)) {
    const normalized: JsonValue[] = [];
    for (const item of object) {
      if (item === undefined) {
        continue;
      }
      normalized.push(serializeToJsonValue(item));
    }
    return normalized;
  }
  if (isPlainObject(object)) {
    const result: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(object)) {
      if (value === undefined) {
        continue;
      }
      result[key] = serializeToJsonValue(value);
    }
    return result;
  }
  throw new TypeError(`Unsupported value type for JSON serialization: ${typeof object}`);
}

export function hasReferencedTweets(object: unknown): object is { referencedTweets?: Array<{ type?: string }> } {
  if (!isPlainObject(object)) {
    return false;
  }
  if (!('referencedTweets' in object)) {
    return false;
  }
  const record: Record<string, unknown> = object;
  const { referencedTweets } = record;
  return referencedTweets === undefined || Array.isArray(referencedTweets);
}

export function isReply(tweet: unknown): boolean {
  if (!hasReferencedTweets(tweet)) return false;
  return Boolean(tweet.referencedTweets?.some((reference) => reference?.type === 'repliedTo'));
}

export function isRetweet(tweet: unknown): boolean {
  if (!hasReferencedTweets(tweet)) return false;
  return Boolean(tweet.referencedTweets?.some((reference) => reference?.type === 'retweeted'));
}

export type Serialized<T> = T extends Date
  ? string
  : T extends bigint
    ? never
    : T extends (...arguments_: unknown[]) => unknown
      ? never
      : T extends object
        ? { [K in keyof T]: Serialized<T[K]> }
        : T;

export function toIsoDatetime(input: unknown): string {
  if (typeof input === 'string' || typeof input === 'number') {
    const normalized = typeof input === 'number' && input < 1e11 ? input * 1000 : input;
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  } else if (isDate(input)) {
    return input.toISOString();
  }
  throw new Error('Invalid date input for toIsoDatetime');
}

export function toDateStrict(input: unknown): Date {
  if (typeof input === 'string' || typeof input === 'number') {
    const normalized = typeof input === 'number' && input < 1e11 ? input * 1000 : input;
    const parsed = new Date(normalized);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  } else if (isDate(input) && !Number.isNaN(input.getTime())) {
    return input;
  }
  throw new Error('Invalid date input for toDateStrict');
}

export function toNullableDate(input: unknown): Date | null {
  if (input === null || input === undefined) {
    return null;
  }
  return toDateStrict(input);
}

export function handleValidationError(error: unknown, context: string): never {
  const errorMessage = error instanceof Error ? error.message : 'Unknown validation error';
  queueMicrotask(() => {
    import('../../logger')
      .then(({ utilsLogger }) => {
        utilsLogger.error('Validation error', { context, error: errorMessage });
      })
      .catch(() => null);
  });
  throw new Error(`Validation failed in ${context}: ${errorMessage}`);
}

export function safeValidation<T>(validator: () => T, context: string, fallback?: T): T | null {
  try {
    return validator();
  } catch (error) {
    if (fallback !== undefined) {
      queueMicrotask(() => {
        import('../../logger')
          .then(({ utilsLogger }) => {
            utilsLogger.warn('Validation error with fallback', {
              context,
              error: error instanceof Error ? error.message : String(error),
            });
          })
          .catch(() => null);
      });
      return fallback;
    }
    queueMicrotask(() => {
      import('../../logger')
        .then(({ utilsLogger }) => {
          utilsLogger.error('Validation error', {
            context,
            error: error instanceof Error ? error.message : String(error),
          });
        })
        .catch(() => null);
    });
    return null;
  }
}
